import { CircularProgress, DialogActions, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import eventsAPI, { Event, EventDetails } from '../../api/events';
import { BR_TEXT, CANCELLATION_REASON } from '../../utils/form';
import { Button, ModalV2, SnackMessage, Text, TextField } from '../common';

interface CancelEventModalProps {
  session?: Event | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const useStyles = makeStyles({
  actions: {
    padding: '32px 0 0',
  },
  inputDescription: {
    marginTop: 15,
  },
});

const getEventDetails = (
  status: string,
  session: Event,
  reason?: string,
): EventDetails => {
  let currentDescription;
  if (reason) {
    currentDescription = `${CANCELLATION_REASON}${reason}${BR_TEXT}${session?.description}`;
  } else {
    currentDescription = session.description;
  }
  return {
    description: currentDescription,
    end: session.end,
    followupReminder: session?.followupReminder,
    id: session.id,
    start: session.start,
    status: status,
    summary: session.summary,
    tenantEventChannelId: session.tenantEventChannelId,
    ventureId: session.ventureId,
    ventureName: session.ventureName,
    emailList: session.emailList,
    physicalLocation: session.physicalLocation,
  };
};

function CancelEventModal({
  session,
  open,
  onClose,
  onSuccess,
}: CancelEventModalProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCancelSession = useCallback(async () => {
    if (!session) {
      return;
    }

    try {
      setIsLoading(true);
      await eventsAPI.update(
        session.id,
        getEventDetails('ARCHIVED', session, reason),
      );
      await eventsAPI.cancelEvent(session.id);
      await eventsAPI.deleteEvent(session.id);
      enqueueSnackbar('The meeting was successfully cancelled.', {
        variant: 'success',
      });
      onClose();
      onSuccess?.();
      setIsLoading(false);
    } catch (error: any) {
      const messageError = error.response?.data?.message;

      enqueueSnackbar(
        'An error occurred at the cancel of the session. Please, try again.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
      setIsLoading(false);
      onClose();
    }
  }, [enqueueSnackbar, onClose, onSuccess, reason, session]);

  return (
    <ModalV2
      width='480px'
      open={open}
      onClose={onClose}
      titleProps={{ title: 'Do you really want to cancel this session?' }}>
      <Text variant='normal'>
        We will send a cancellation note to all session participants and move
        this session to Archive. Do you want to proceed?
      </Text>
      <TextField
        small
        name='reason'
        label='Reason'
        data-testid='modal-reason-input'
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className={classes.inputDescription}
      />
      <DialogActions className={classes.actions}>
        <Button
          variant='outlined'
          onClick={onClose}
          data-testid='button-cancel'>
          No
        </Button>
        <Button
          onClick={handleCancelSession}
          disabled={isLoading}
          data-testid='button-success'>
          {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Yes'}
        </Button>
      </DialogActions>
    </ModalV2>
  );
}

export default CancelEventModal;
